import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Lock, Mail, Phone, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo.webp";
import { useSiteAssetUrl } from "@/hooks/useSiteAssets";
import { InstagramFeed } from "./InstagramFeed";
import { buildWhatsAppUrl } from "@/lib/leadSource";

const WHATSAPP_MSG = "Olá! Vim pelo site do Atacado Óptico e quero saber as condições de atacado.";

function FooterAccordion({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const id = title.toLowerCase().replace(/\s+/g, "-");

  if (!isMobile) {
    return (
      <div>
        <h4 className="font-heading font-semibold text-primary-foreground mb-3">{title}</h4>
        {children}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={`footer-${id}`}
        className="w-full flex items-center justify-between py-2 text-left min-h-[44px]"
      >
        <h4 className="font-heading font-semibold text-primary-foreground">{title}</h4>
        <ChevronDown className={`h-4 w-4 text-primary-foreground/60 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <div
        id={`footer-${id}`}
        className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        {children}
      </div>
    </div>
  );
}

export function Footer() {
  const isMobile = useIsMobile();
  const logoUrl = useSiteAssetUrl("logo", logo);

  return (
    <footer role="contentinfo" className="bg-primary text-primary-foreground/85 mt-auto">

      {/* ── MAP STRIP ── */}
      {!isMobile ? (
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <iframe
            title="Localização Atacado Óptico"
            src="https://www.openstreetmap.org/export/embed.html?bbox=-43.9416%2C-19.9218%2C-43.9357%2C-19.9178&layer=mapnik&marker=-19.9198%2C-43.9386"
            className="w-full h-full border-0 grayscale"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          {/* Info card overlay */}
          <div className="absolute bottom-4 left-4 bg-white rounded-xl shadow-lg px-4 py-3 max-w-xs">
            <p className="font-heading font-bold text-foreground text-sm">Atacado Óptico</p>
            <p className="text-xs text-muted-foreground leading-snug mt-0.5">
              Av. Afonso Pena, 726, Sala 800<br/>
              Centro (Praça Sete) · BH/MG · CEP 30.130-902
            </p>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Av.+Afonso+Pena,+726,+Centro,+Belo+Horizonte,+MG"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-accent hover:underline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
              Como chegar
            </a>
          </div>
        </div>
      ) : (
        <div className="bg-primary/80 py-4 px-4 text-center">
          <p className="text-primary-foreground/80 text-sm mb-2">
            Av. Afonso Pena, 726, Sala 800, Centro, BH/MG
          </p>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Av.+Afonso+Pena,+726,+Centro,+Belo+Horizonte,+MG"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-accent text-white text-sm font-semibold px-4 min-h-11 py-3 rounded-lg hover:bg-accent/90 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
            Ver no Google Maps
          </a>
        </div>
      )}

      <InstagramFeed />

      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <img src={logoUrl} alt="Atacado Óptico - Logo" className="h-12 w-auto mb-4" />
            <p className="text-sm">
              Seu parceiro no mercado óptico. Qualidade, variedade e os melhores preços para sua loja.
            </p>
            <p className="text-xs mt-2">CNPJ: 17.393.408/0001-73</p>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://instagram.com/atacado_optico" target="_blank" rel="noopener noreferrer" aria-label="Instagram @atacado_optico" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://facebook.com/atacadoopticomg" target="_blank" rel="noopener noreferrer" aria-label="Facebook do Atacado Óptico" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://linkedin.com/company/atacado-optico" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn do Atacado Óptico" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://youtube.com/@atacadooptico" target="_blank" rel="noopener noreferrer" aria-label="YouTube do Atacado Óptico" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58a2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>
              </a>
              <a href={buildWhatsAppUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp do Atacado Óptico" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true"><path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.376L1.056 31.2l6.064-1.952A15.9 15.9 0 0 0 16.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.31 22.608c-.39 1.098-1.932 2.01-3.15 2.276-.834.178-1.924.32-5.594-1.202-4.694-1.946-7.71-6.716-7.944-7.026-.226-.31-1.846-2.462-1.846-4.694 0-2.232 1.168-3.33 1.584-3.784.39-.424.912-.6 1.2-.6.152 0 .286.008.408.014.416.018.624.042.898.694.342.814 1.176 2.874 1.278 3.082.104.21.202.478.076.768-.118.296-.22.478-.432.738-.21.258-.44.458-.652.74-.19.244-.406.506-.172.922.234.414 1.04 1.716 2.234 2.782 1.536 1.372 2.83 1.798 3.23 1.998.4.2.634.168.868-.1.242-.276 1.034-1.2 1.31-1.614.274-.414.548-.346.922-.208.378.138 2.392 1.13 2.802 1.336.41.208.682.31.782.478.098.168.098.978-.292 2.078z"/></svg>
              </a>
            </div>
          </div>
          <FooterAccordion title="Navegação">
            <ul className="space-y-2 text-sm">
              <li><Link to="/catalogo?categoria=oculos" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Óculos</Link></li>
              <li><Link to="/catalogo?categoria=acessorios" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Acessórios</Link></li>
              <li><Link to="/catalogo?categoria=equipamentos" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Equipamentos</Link></li>
              <li><Link to="/lentes-grade" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Lentes</Link></li>
              <li><Link to="/simulador-publico" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Simulador de Lentes</Link></li>
              <li><Link to="/surfacado" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Lentes Surfaçadas</Link></li>
              <li><Link to="/pedido-laboratorio" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Pedido ao Laboratório</Link></li>
              <li><Link to="/crie-sua-marca" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Crie Sua Marca</Link></li>
              <li><Link to="/tabela-precos" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Tabela de Preços</Link></li>
              <li><Link to="/blog" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Blog</Link></li>
              <li><a href="/rss.xml" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">RSS do Blog</a></li>
              <li><Link to="/politica-de-devolucao" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Política de Trocas e Devoluções</Link></li>

            </ul>
          </FooterAccordion>
          <FooterAccordion title="Atacado e Revenda">
            <ul className="space-y-2 text-sm">
              <li><Link to="/fornecedor-para-oticas" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Fornecedor para Óticas</Link></li>
              <li><Link to="/atacado-de-oculos" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Atacado de Óculos</Link></li>
              <li><Link to="/oculos-para-revenda" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Óculos para Revenda</Link></li>
              <li><Link to="/fornecedor-de-lentes" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Fornecedor de Lentes</Link></li>
              <li><Link to="/acessorios-para-oticas" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Acessórios para Óticas</Link></li>
              <li><Link to="/para-sua-otica" className="hover:text-accent transition-colors inline-block min-h-[44px] flex items-center md:min-h-0">Para sua Ótica</Link></li>
            </ul>
          </FooterAccordion>
          <FooterAccordion title="Contato">
            <ul className="space-y-2 text-sm">
              <li>
                <a href="mailto:atendimento@atacadooptico.com.br" className="hover:text-accent transition-colors flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                  atendimento@atacadooptico.com.br
                </a>
              </li>
              <li>
                <a href="mailto:atacadoopticomg@gmail.com" className="hover:text-accent transition-colors flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                  atacadoopticomg@gmail.com
                </a>
              </li>
              <li>
                <a href="tel:+553125383333" className="hover:text-accent transition-colors flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Tel: (31) 2538-3333
                </a>
              </li>
              <li>
                <a href={buildWhatsAppUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-4 w-4 fill-current shrink-0" aria-hidden="true">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.376L1.056 31.2l6.064-1.952A15.9 15.9 0 0 0 16.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.31 22.608c-.39 1.098-1.932 2.01-3.15 2.276-.834.178-1.924.32-5.594-1.202-4.694-1.946-7.71-6.716-7.944-7.026-.226-.31-1.846-2.462-1.846-4.694 0-2.232 1.168-3.33 1.584-3.784.39-.424.912-.6 1.2-.6.152 0 .286.008.408.014.416.018.624.042.898.694.342.814 1.176 2.874 1.278 3.082.104.21.202.478.076.768-.118.296-.22.478-.432.738-.21.258-.44.458-.652.74-.19.244-.406.506-.172.922.234.414 1.04 1.716 2.234 2.782 1.536 1.372 2.83 1.798 3.23 1.998.4.2.634.168.868-.1.242-.276 1.034-1.2 1.31-1.614.274-.414.548-.346.922-.208.378.138 2.392 1.13 2.802 1.336.41.208.682.31.782.478.098.168.098.978-.292 2.078z" />
                  </svg>
                  WhatsApp: (31) 98501-5555
                </a>
              </li>
              <li className="text-xs pt-1">Av. Afonso Pena 726 sl. 800 Centro (Praça Sete)<br/>Belo Horizonte - MG CEP 30.130-902</li>
            </ul>
          </FooterAccordion>
        </div>
        <div className="border-t border-primary-foreground/10 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <span>© {new Date().getFullYear()} Atacado Óptico. Todos os direitos reservados.</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-primary-foreground/75">
              <Lock className="h-3 w-3" aria-hidden="true" />
              Site Seguro SSL
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
